/**
 * The one-tap hide-values switch (V5-03), mounted in the top bar next to the
 * theme toggle.
 *
 * "Reachable in one tap" is a requirement, not a nicety: a setting that lives
 * three screens deep is a setting nobody uses when the person next to them
 * leans over. It sits in the chrome, visible on every screen including mobile,
 * and its state is legible at a glance from the icon alone.
 */
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePrivacy } from "./store";
import { useSavePrivacy } from "./PrivacyProvider";

export function PrivacyToggle({
  className,
  variant = "chrome",
}: {
  className?: string;
  /** `chrome` = the navy top bar; `surface` = a light card. Mirrors
   * `ThemeToggle`, so the two buttons beside each other match exactly. */
  variant?: "surface" | "chrome";
}) {
  const { hideValues } = usePrivacy();
  const save = useSavePrivacy();

  return (
    <button
      type="button"
      onClick={() => save({ hideValues: !hideValues })}
      aria-pressed={hideValues}
      title={hideValues ? "Show amounts" : "Hide amounts"}
      aria-label={hideValues ? "Show amounts" : "Hide amounts"}
      className={cn(
        variant === "chrome"
          ? "inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-[var(--chrome-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cp-accent)]"
          : "cp-icon-btn h-9 w-9 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cp-accent-wash)]",
        // Lit when ON, so "amounts are hidden right now" is readable without
        // hovering for the tooltip.
        variant === "chrome" && hideValues
          ? "bg-[var(--chrome-hover)] text-white"
          : variant === "chrome"
            ? "text-[var(--chrome-text-2)]"
            : "",
        className,
      )}
    >
      {hideValues ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );
}
